import { useCallback } from 'react';
import { Input } from '@design-system/components/input/input';
import { Col, FormLabel, Row } from '@design-system/components/grid/grid';
import { FormLabelSuperTooltip } from './FormLabelSuperTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import {
  faInstagram,
  faTiktok,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { ProfileVM } from '../../view-model/Profile.viewModel';
import { TextareaWithCounter } from './TextareaWithCounter';
import * as styles from './StorySection.css';

type StorySectionProps = {
  vm: ProfileVM;
};

const STORY_MAX_LENGTH = 800;
const ACCOMPLISHMENTS_MAX_LENGTH = 500;

export const StorySection = ({ vm }: StorySectionProps) => {
  const { form, aiAssist } = vm;

  const storyValue = form.watch('messageProfile') ?? '';
  const accomplishmentsValue = form.watch('accomplishments') ?? '';

  const handleUndo = useCallback(() => {
    const undoData = aiAssist.undo();
    if (!undoData) return;

    form.setValue(undoData.formField, undoData.previousText, {
      shouldDirty: true,
    });
  }, [aiAssist, form]);

  const isAiDisabled = vm.isSaving;

  return (
    <div className={styles.root}>
      <Col>
        <FormLabelSuperTooltip templateId="profile-story">
          Story
        </FormLabelSuperTooltip>
        <TextareaWithCounter
          {...form.register('messageProfile')}
          rows={5}
          maxLength={STORY_MAX_LENGTH}
          currentLength={storyValue.length}
          placeholder="Who you are and why should users subscribe to you?"
          error={form.errors.messageProfile?.message}
          footerLeft={
            <button
              type="button"
              className={styles.aiButton}
              disabled={isAiDisabled}
              onClick={() => aiAssist.open('BIO', storyValue)}
            >
              Improve with AI
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                className={styles.aiButtonIcon}
              />
            </button>
          }
        />
        {aiAssist.undoAvailable?.formField === 'messageProfile' && (
          <div className={styles.undoBar}>
            <span>Text replaced by AI</span>
            <button
              type="button"
              className={styles.undoButton}
              onClick={handleUndo}
            >
              Undo
            </button>
          </div>
        )}
      </Col>

      <Col>
        <FormLabelSuperTooltip templateId="accomplishments">
          Accomplishments
        </FormLabelSuperTooltip>
        <TextareaWithCounter
          {...form.register('accomplishments')}
          rows={5}
          maxLength={ACCOMPLISHMENTS_MAX_LENGTH}
          currentLength={accomplishmentsValue.length}
          placeholder="Who you are and why should users subscribe to you?"
          error={form.errors.accomplishments?.message}
          footerLeft={
            <button
              type="button"
              className={styles.aiButton}
              disabled={isAiDisabled}
              onClick={() =>
                aiAssist.open('ACCOMPLISHMENTS', accomplishmentsValue)
              }
            >
              Improve with AI
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                className={styles.aiButtonIcon}
              />
            </button>
          }
        />
        {aiAssist.undoAvailable?.formField === 'accomplishments' && (
          <div className={styles.undoBar}>
            <span>Text replaced by AI</span>
            <button
              type="button"
              className={styles.undoButton}
              onClick={handleUndo}
            >
              Undo
            </button>
          </div>
        )}
      </Col>

      <Row gap="xl" className={styles.socialRow}>
        <div>
          <FormLabel required icon={faInstagram}>
            Instagram
          </FormLabel>
          <Input
            {...form.register('profileInstagram')}
            startCta={<span className={styles.atPrefix}>@</span>}
            error={form.errors.profileInstagram?.message}
          />
        </div>
        <div>
          <FormLabel icon={faTiktok}>TikTok</FormLabel>
          <Input
            {...form.register('profileTiktok')}
            startCta={<span className={styles.atPrefix}>@</span>}
            error={form.errors.profileTiktok?.message}
          />
        </div>
        <div>
          <FormLabel icon={faYoutube}>Youtube</FormLabel>
          <Input
            {...form.register('profileYoutubeChannel')}
            placeholder="https://www.youtube.com/channel/your_channel"
            error={form.errors.profileYoutubeChannel?.message}
          />
        </div>
      </Row>
    </div>
  );
};
